import React, { useState } from "react";
import { motion } from "framer-motion";
import { CurrencyInr, Lightning, ChartLineUp, Leaf, ArrowRight, Calculator as CalcIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import api, { formatApiErrorDetail } from "../lib/api";

export default function Calculator() {
  const [step, setStep] = useState(1); // 1 = bill, 2 = lead, 3 = result
  const [bill, setBill] = useState("");
  const [lead, setLead] = useState({ name: "", phone: "", city: "Akola" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const goStep2 = (e) => {
    e.preventDefault();
    const b = Number(bill);
    if (!b || b < 500) {
      toast.error("Please enter a bill amount (minimum ₹500)");
      return;
    }
    setStep(2);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !/^[6-9]\d{9}$/.test(lead.phone)) {
      toast.error("Enter valid name and 10-digit mobile");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        ...lead,
        electricity_bill: bill,
        source: "calculator",
      });
      const { data } = await api.post("/calculator/estimate", { monthly_bill: Number(bill) });
      setResult(data);
      setStep(3);
      toast.success("Here's your custom solar estimate!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1); setBill(""); setLead({ name:"",phone:"",city:"Akola"}); setResult(null);
  };

  return (
    <div data-testid="calculator-page">
      <SEO page="calculator" titleFallback="Solar Calculator — Orax Solar"/>

      <section className="pt-32 pb-16 bg-[#F9F8F6]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="overline mb-3 flex items-center justify-center gap-2"><CalcIcon weight="duotone" size={16}/> SOLAR CALCULATOR</div>
            <h1 className="h1 mb-5">Find your solar savings in 30 seconds</h1>
            <p className="text-lg text-[#2D4739]">Enter your monthly electricity bill and get a customized solar recommendation with cost, subsidy and payback details.</p>
          </div>
        </div>
      </section>

      <section className="pb-24 bg-[#F9F8F6]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-3xl p-8 md:p-12 border border-[#E5E3DB] shadow-xl">
              {/* Progress */}
              <div className="flex items-center gap-2 mb-8">
                {[1, 2, 3].map(s => (
                  <div key={s} className={`flex-1 h-1.5 rounded-full ${step >= s ? "bg-[#135934]" : "bg-[#E5E3DB]"}`}/>
                ))}
              </div>

              {step === 1 && (
                <motion.form initial={{opacity:0}} animate={{opacity:1}} onSubmit={goStep2} data-testid="calc-step-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#135934] mb-2">STEP 1 OF 3</div>
                  <h2 className="h2 mb-2">What's your monthly electricity bill?</h2>
                  <p className="text-[#5C7368] mb-6">Average of last 3-6 months works best.</p>
                  <div className="relative mb-4">
                    <CurrencyInr size={28} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#5C7368]"/>
                    <input
                      type="number"
                      value={bill}
                      onChange={e => setBill(e.target.value)}
                      placeholder="e.g. 5000"
                      className="input-solar w-full pl-14 pr-5 py-5 rounded-2xl border-2 border-[#E5E3DB] text-3xl font-bold text-[#0A1F15] placeholder:text-[#5C7368]/50 placeholder:font-normal placeholder:text-xl"
                      autoFocus
                      data-testid="calc-bill-input"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {[1500, 3000, 5000, 8000, 15000, 50000].map(v => (
                      <button key={v} type="button" onClick={() => setBill(String(v))} className="px-4 py-2 rounded-full bg-[#F0EFEB] hover:bg-[#E5E3DB] text-sm font-medium">
                        ₹{v.toLocaleString()}
                      </button>
                    ))}
                  </div>
                  <button type="submit" className="btn-primary w-full text-base" data-testid="calc-next-1">
                    Continue <ArrowRight weight="bold" size={18}/>
                  </button>
                </motion.form>
              )}

              {step === 2 && (
                <motion.form initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} onSubmit={submitLead} data-testid="calc-step-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#135934] mb-2">STEP 2 OF 3</div>
                  <h2 className="h2 mb-2">Almost done — one more step</h2>
                  <p className="text-[#5C7368] mb-6">We'll also send you a detailed quote and solar expert will call back.</p>
                  <div className="grid gap-3 mb-6">
                    <input
                      type="text"
                      placeholder="Your Name *"
                      value={lead.name}
                      onChange={e => setLead({...lead, name:e.target.value})}
                      className="input-solar w-full px-5 py-4 rounded-xl border border-[#E5E3DB] text-lg"
                      required
                      data-testid="calc-name"
                    />
                    <input
                      type="tel"
                      placeholder="10-digit Mobile *"
                      value={lead.phone}
                      onChange={e => setLead({...lead, phone:e.target.value})}
                      maxLength={10}
                      className="input-solar w-full px-5 py-4 rounded-xl border border-[#E5E3DB] text-lg"
                      required
                      data-testid="calc-phone"
                    />
                    <input
                      type="text"
                      placeholder="City"
                      value={lead.city}
                      onChange={e => setLead({...lead, city:e.target.value})}
                      className="input-solar w-full px-5 py-4 rounded-xl border border-[#E5E3DB] text-lg"
                      data-testid="calc-city"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setStep(1)} className="btn-outline-dark">Back</button>
                    <button type="submit" disabled={loading} className="btn-primary flex-1 disabled:opacity-60" data-testid="calc-submit">
                      {loading ? "Calculating..." : <>Show My Savings <ArrowRight weight="bold" size={18}/></>}
                    </button>
                  </div>
                </motion.form>
              )}

              {step === 3 && result && (
                <motion.div initial={{opacity:0,scale:0.98}} animate={{opacity:1,scale:1}} data-testid="calc-result">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#135934] mb-2">YOUR CUSTOM ESTIMATE</div>
                  <h2 className="h2 mb-6">You could save <span className="text-[#135934]">₹{(result.twenty_five_year_savings/100000).toFixed(1)}L</span> over 25 years</h2>

                  <div className="grid sm:grid-cols-2 gap-4 mb-6">
                    <StatCard icon={<Lightning weight="duotone" size={24}/>} label="System Size" value={`${result.system_size_kw} kW`} color="blue"/>
                    <StatCard icon={<CurrencyInr weight="duotone" size={24}/>} label="Net Investment" value={`₹${result.net_cost.toLocaleString()}`} color="green"/>
                    <StatCard icon={<ChartLineUp weight="duotone" size={24}/>} label="Yearly Savings" value={`₹${result.yearly_savings.toLocaleString()}`} color="green"/>
                    <StatCard icon={<Leaf weight="duotone" size={24}/>} label="Payback" value={`${result.payback_years} years`} color="blue"/>
                  </div>

                  <div className="bg-[#F0EFEB] rounded-2xl p-6 mb-6">
                    <div className="text-sm font-semibold text-[#5C7368] mb-3">Cost breakdown</div>
                    <div className="space-y-2">
                      <Row k="System cost" v={`₹${result.gross_cost.toLocaleString()}`}/>
                      <Row k="Subsidy (PM Surya Ghar)" v={`− ₹${result.subsidy.toLocaleString()}`} accent/>
                      <div className="border-t border-[#E5E3DB] pt-2"/>
                      <Row k="Your net investment" v={`₹${result.net_cost.toLocaleString()}`} bold/>
                      <Row k="Monthly savings" v={`₹${result.monthly_savings.toLocaleString()}`}/>
                      <Row k="CO₂ saved / year" v={`${result.co2_savings_tons_per_year} tons`}/>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <a href="tel:9422921481" className="btn-primary flex-1 justify-center">Call me — let's proceed</a>
                    <button onClick={reset} className="btn-outline-dark">Recalculate</button>
                  </div>

                  <p className="text-xs text-[#5C7368] mt-5 text-center">
                    * Estimates based on average Maharashtra tariff (~₹9/unit) and 120 units/kW/month generation. Actual values may vary.
                  </p>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const bg = color === "green" ? "bg-[#135934]/10 text-[#135934]" : "bg-[#0F52BA]/10 text-[#0F52BA]";
  return (
    <div className="bg-white border border-[#E5E3DB] rounded-2xl p-5">
      <div className={`inline-flex w-10 h-10 rounded-lg ${bg} items-center justify-center mb-3`}>{icon}</div>
      <div className="text-sm text-[#5C7368] mb-1">{label}</div>
      <div className="font-display font-extrabold text-2xl text-[#0A1F15]">{value}</div>
    </div>
  );
}

function Row({ k, v, bold, accent }) {
  return (
    <div className={`flex justify-between ${bold ? "font-bold text-[#0A1F15] text-lg" : "text-[#2D4739]"}`}>
      <span>{k}</span>
      <span className={accent ? "text-[#135934] font-bold" : ""}>{v}</span>
    </div>
  );
}
